import type { ComponentType } from "react";

import {
  FigmaMark,
  GithubMark,
  GmailMark,
  JoyloSsoMark,
  OutlookMark,
  SmtpMark,
  StripeMark,
  TeamsMark,
  UniversalKeyMark,
  type BrandIcon,
} from "@/components/Connectors/brandIcons";
import {
  AtSign,
  Ban,
  CreditCard,
  Figma,
  FileText,
  Github,
  HardDrive,
  Inbox,
  KeyRound,
  Mail,
  MessageCircle,
  MessageSquare,
  NotebookPen,
  Phone,
  Plug,
  Send,
  Server,
  ShieldCheck,
  SquareKanban,
  Table2,
  Users,
} from "lucide-react";

// One slug-to-component map, resolved from the `icon` the server sends rather than from the
// connector key. The dialog used to switch on the key itself, so a connector added server-side
// rendered a generic plug — and, worse, a button that did nothing. Adding a connector now only
// needs a slug that already exists here, and an unknown slug degrades to a plug rather than crashing.

export type IconComponent = ComponentType<{ className?: string }>;

const ICONS: Record<string, IconComponent> = {
  figma: Figma,
  github: Github,
  stripe: CreditCard,
  shield: ShieldCheck,
  key: KeyRound,
  drive: HardDrive,
  docs: FileText,
  sheets: Table2,
  gmail: Mail,
  slack: MessageSquare,
  jira: SquareKanban,
  notion: NotebookPen,
  inbox: Inbox,
  plug: Plug,
  blocked: Ban,
  // Messaging services, present ahead of their connectors so adding one server-side does not
  // render a generic plug on its first day
  smtp: Server,
  outlook: Mail,
  teams: Users,
  twilio: Phone,
  sms: MessageCircle,
  whatsapp: MessageCircle,
  discord: MessageSquare,
  telegram: Send,
  email: AtSign,
};

/**
 * The icon for a slug: the brand mark where one exists, else a glyph, else a plug.
 *
 * Brand marks come first because a generic envelope for both Gmail and Outlook made the two
 * indistinguishable in a grid, which is the one thing a logo is for. The glyph map stays as the
 * fallback so a connector added server-side still renders something sensible on its first day.
 */
// Brand marks, keyed by both the connector key and the icon slug it declares — joylo_sso sends
// icon="shield", so both spellings have to land on the same mark.
export const BRAND_ICONS: Record<string, BrandIcon> = {
  gmail: GmailMark,
  outlook: OutlookMark,
  teams: TeamsMark,
  smtp: SmtpMark,
  stripe: StripeMark,
  github: GithubMark,
  figma: FigmaMark,
  joylo_sso: JoyloSsoMark,
  shield: JoyloSsoMark,
  universal_ai_key: UniversalKeyMark,
  key: UniversalKeyMark,
};

/** The brand mark for a slug, or undefined so the caller can fall back to a glyph. */
export const brandIconFor = (slug?: string): BrandIcon | undefined =>
  slug ? BRAND_ICONS[slug] : undefined;

export const iconFor = (slug?: string): IconComponent =>
  brandIconFor(slug) || (slug && ICONS[slug]) || Plug;

export default ICONS;
